import { AgreeToTermsPolicy, experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import Divider from '@/components/Divider';
import GoogleOneTap from '@/components/GoogleOneTap';
import TextLink from '@/components/TextLink';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import ErrorPage from '../ErrorPage';

import Main from './Main';
import styles from './index.module.scss';

const SignInFooters = () => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { signInMethods, signUpMethods, socialConnectors, signInMode, singleSignOnEnabled } =
    useSieMethods();

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    /**
     * Check if the user has agreed to the terms and privacy policy before navigating to the SSO page
     * when the policy is set to `Manual`
     */
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  /* Hide footers when showing Single Sign On form */
  if (showSingleSignOnForm) {
    return null;
  }

  return (
    <>
      {
        // Single Sign On footer
        singleSignOnEnabled && (
          <>
            <div className={styles.singleSignOn}>
              {t('description.use')}{' '}
              <TextLink text="action.single_sign_on" onClick={handleSsoNavigation} />
            </div>
            {
              /**
               * If only SSO sign-in methods are available, display the agreement checkbox when the agreement policy is `Manual`.
               */
              signInMethods.length === 0 &&
                socialConnectors.length === 0 &&
                agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
                  <TermsAndPrivacyCheckbox className={styles.checkboxForSsoOnly} />
                )
            }
          </>
        )
      }
      {
        // Create Account footer
        signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && (
          <div className={styles.createAccount}>
            {t('description.no_account')}{' '}
            <TextLink replace to="/register" text="action.create_account" />
          </div>
        )
      }
      {
        // Social sign-in methods
        signInMethods.length > 0 && socialConnectors.length > 0 && (
          <>
            <Divider label="description.or" className={styles.divider} />
            <SocialSignInList socialConnectors={socialConnectors} className={styles.main} />
          </>
        )
      }
    </>
  );
};

const ShowEmailDomain = () => {
  // Const handleClick = () => {
  //   toast.custom(
  //     (t) => (
  //       <div style={{
  //         padding: '16px',
  //         background: '#fff',
  //         borderRadius: '8px',
  //         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  //         maxWidth: '300px',
  //         position: 'relative'
  //       }}>
  //         <button
  //           onClick={() => toast.dismiss(t.id)}
  //           style={{
  //             position: 'absolute',
  //             right: '8px',
  //             top: '8px',
  //             background: 'none',
  //             border: 'none',
  //             cursor: 'pointer',
  //             fontSize: '16px',
  //             color: '#666'
  //           }}
  //         >
  //           ×
  //         </button>
  //         <div style={{
  //           fontSize: '16px',
  //           fontWeight: 'bold',
  //           marginBottom: '8px'
  //         }}>
  //           支持的公司有：
  //         </div>
  //         <div style={{
  //           marginBottom: '12px'
  //         }}>
  //           {'腾讯、阿里巴巴、字节跳动、华为、百度、苹果、微软、京东、美团、拼多多、快手、小米、谷歌、亚马逊、Meta、网易、Shopee、滴滴、携程、小红书、B站、爱奇艺、米哈游、荣耀、好未来、作业帮、去哪儿、新浪、汽车之家、陌陌、58同城、知乎、金山云、转转、Zoom、喜马拉雅、猿辅导、昆仑万维、得物、360、蔚来、顺丰、英伟达、理想'}
  //         </div>
  //         <div style={{
  //           fontSize: '14px',
  //           color: '#666',
  //           borderTop: '1px solid #eee',
  //           paddingTop: '8px'
  //         }}>
  //           更多公司接入请联系我们
  //         </div>
  //       </div>
  //     ),
  //     {
  //       duration: 5000,
  //       position: 'top-center',
  //     }
  //   );
  // };

  return (
    <div className={styles.sloganheader}>
      <div className={styles.slogan}>只向互联网/科技/金融/体制内职员开放的脱单平台</div>
      {/* <div className={styles.emailList} onClick={handleClick}>
        支持公司列表{'>>'}
      </div> */}
    </div>
  );
};

const SignIn = () => {
  const { signInMethods, socialConnectors, signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const [params] = useSearchParams();

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  return (
    <LandingPageLayout title="description.sign_in_to_your_account">
      <GoogleOneTap context="signin" />
      {/* <Toaster position="top-center" /> */}
      <ShowEmailDomain />
      <SingleSignOnFormModeContextProvider>
        <Main signInMethods={signInMethods} socialConnectors={socialConnectors} />
        <SignInFooters />
      </SingleSignOnFormModeContextProvider>
      {
        // Only show terms and privacy links for sign in page if the agree to terms policy is `Automatic` or `ManualRegistrationOnly`
        agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
          <TermsAndPrivacyLinks className={styles.terms} />
        )
      }
    </LandingPageLayout>
  );
};

export default SignIn;
